import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsGuestsV1EventGuestsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/events-guests',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/events-guests',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-guests',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/events-guests',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/events-guests',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/events-guests',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events/v2/guests',
        destPath: '/v2/guests',
      },
      {
        srcPath: '/events-guests/v2/guests',
        destPath: '/v2/guests',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_guests';

/**
 * Creates a query to retrieve a list of guests.
 *
 *
 * The `queryGuests()` method builds a query to retrieve a list of guests and returns a [GuestsQueryBuilder](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/ascending) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/find) method.
 *
 * You can refine the query by chaining `GuestsQueryBuilder` methods onto the query. `GuestsQueryBuilder` methods enable you to sort, filter, and control the results that `queryGuests.find()` returns.
 *
 * The query runs with the following `GuestsQueryBuilder` defaults that you can override:
 *
 * - [`skipTo(0)`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/skip-to)
 * - [`limit(50)`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/limit)
 * - [`descending("_createdDate")`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/descending)
 *
 * The methods that are chained to `queryGuests()` are applied in the order they are called. For example, if you apply `ascending ('_createdDate')` and then `descending ('_updatedDate')`, the results are sorted first by the created date and then, if there are multiple results with the same date, the items are sorted by the updated date.
 *
 * The table below shows which `GuestsQueryBuilder` methods are supported for `queryGuests()`. You can only use one filter method for each property. Only the first filter will work if a property is used in more than one filter.
 */
export function queryEventGuests(payload: object): RequestOptionsFactory<any> {
  function __queryEventGuests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.guests.v1.guest',
      method: 'POST' as any,
      methodFqn: 'wix.events.guests.v1.EventGuestsService.QueryEventGuests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsGuestsV1EventGuestsServiceUrl({
        protoPath: '/v2/guests/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'guests.createdDate' },
              { path: 'guests.updatedDate' },
              { path: 'guests.attendanceStatusUpdatedDate' },
              { path: 'guests.guestDetails.formResponse.inputValues.dateTime' },
              { path: 'guests.additionalDetails.createdDate' },
              { path: 'guests.additionalDetails.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'guests.guestDetails.formResponse.inputValues.number' },
              {
                path: 'guests.guestDetails.formResponse.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'guests.guestDetails.formResponse.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryEventGuests;
}
